import PropTypes from 'prop-types';
import { createContext, useContext } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
//
import Icon from './Icon';
import {
  MOTION,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  fieldInkOf,
  fieldLineOf,
  focusRingSx,
  hairlineOf,
  sunkenOf,
  tabularSx
} from 'src/theme/universe/brand';
import { ghostButtonSx } from 'src/theme/universe/buttonSx';

// ---------------------------------------------------------------------------
// THE SURFACES OF A RECORD (DESIGN.md §7, §14 — revised 07/10/2026 evening):
// a record is a composition of REGION SHEETS on the canvas, one per layer,
// each read through its head and its rows — never a card per widget inside
// a sheet. These are the pieces it is built from:
//
//   SectionHead   title 16/700 · one meta line · an action, a hairline under
//   Section       a region sheet with its head and its body (flat when it is
//                 rendered inside another section)
//   Well          a sunken region (the economics board), no border, no shadow
//   Eyebrow       the group label (13/600, sentence case) naming a group of
//                 facts — the 11px uppercase eyebrow retired on 08/10/2026
//   Fact          label 116px · value right-aligned, a hairline between facts
//   heads         the quiet head of a SECONDARY surface and the meta-only
//                 column labels of an EMBEDDED one
//
// Built for the translation project workspace on 07/10/2026; the event page
// keeps `components/record` until it migrates, so nothing here is imported
// by a page that has not chosen the Universe skin.
// ---------------------------------------------------------------------------

const isLight = (theme) => theme.palette.mode === 'light';

export const sectionTitleSx = {
  m: 0,
  minWidth: 0,
  fontFamily: UNIVERSE_FONT,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: 0,
  color: 'text.primary'
};

export const metaSx = {
  fontFamily: UNIVERSE_FONT,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.4,
  color: 'text.secondary'
};

/**
 * The `label` type of DESIGN.md §3 — 13/600 `text.primary`: a form label, a
 * column head, a fact label. A field drawn by MUI gets it from
 * `formControlsSx`; this is for the labels a form writes itself, above a
 * control MUI does not label (the rich-text editors, a group of toggles).
 */
export const labelTypeSx = {
  display: 'block',
  fontFamily: UNIVERSE_FONT,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  color: 'text.primary'
};

export const dataSx = {
  fontFamily: UNIVERSE_FONT,
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.45,
  color: 'text.primary'
};

export const bodyStrongSx = {
  fontFamily: UNIVERSE_FONT,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.5,
  color: 'text.primary'
};

export const subsectionSx = {
  m: 0,
  fontFamily: UNIVERSE_FONT,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.4,
  color: 'text.primary'
};

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// THE REGION SHEET (DESIGN.md §7, revised 07/10/2026 evening). The first cut
// laid every layer straight on the canvas, ruled by hairlines alone; the
// owner's verdict on the rendered page — "too melted with the background,
// difficult to interpret, not premium" — made each layer a sheet: paper, the
// hairline, the small navy shadow (the lit top edge on the night), radius
// `panel`, a 52px head and a 20px inset. The current work is the one
// emphasised sheet: the brand gradient as a 3px top edge, the signature of
// the active context (§2). A sheet never holds another sheet: a Section
// rendered inside a Section draws itself flat — head, hairline, body — so a
// panel that names its own sections inside the current work cannot nest
// boxes by accident.

const SheetContext = createContext(false);

/** The inset of a sheet: what its head and its body keep from the edge. */
export const SHEET_INSET = { xs: 2, sm: 2.5 };

/** A region sheet: stratum 1 with the small shadow; `emphasis` adds the gradient edge. */
export const regionSheetSx = (theme, { emphasis = false } = {}) => ({
  position: 'relative',
  minWidth: 0,
  bgcolor: 'background.paper',
  // the edge in the field line, a step firmer than the hairline, and a soft
  // two-layer shadow (the owner, 08/10/2026: "still melted with the bg")
  border: `1px solid ${fieldLineOf(theme)}`,
  borderRadius: `${RADIUS.panel}px`,
  boxShadow: isLight(theme)
    ? '0 1px 2px rgba(1,38,109,.05), 0 2px 8px -2px rgba(1,38,109,.08)'
    : '0 1px 2px rgba(0,0,0,.45), 0 2px 8px -2px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.07)',
  overflow: 'hidden',
  ...(emphasis && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      backgroundImage: UNIVERSE.gradient,
      pointerEvents: 'none'
    }
  })
});

SectionHead.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  action: PropTypes.node,
  component: PropTypes.elementType,
  /** Inside a sheet: the 52px head, inset like the body. */
  inset: PropTypes.bool,
  sx: PropTypes.object
};

/**
 * Title + meta + action on one line, a hairline beneath. Where the line is
 * too narrow for both (a phone), the action drops under the title, still on
 * the right, instead of squeezing the meta into a column of single words.
 */
export function SectionHead({ title, meta, action, component = 'h2', inset = false, sx }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      sx={{
        minHeight: inset ? 52 : 36,
        rowGap: 1,
        ...(inset ? { px: SHEET_INSET, py: 1.25 } : { pb: 1 }),
        borderBottom: `1px solid ${hairlineOf(theme)}`,
        ...sx
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ minWidth: 0, flex: '1 1 240px' }}
      >
        <Typography component={component} sx={sectionTitleSx}>
          {title}
        </Typography>
        {meta && (
          <Typography component="span" sx={{ ...metaSx, minWidth: 0 }}>
            {meta}
          </Typography>
        )}
      </Stack>
      {action && <Box sx={{ flex: 'none', ml: 'auto' }}>{action}</Box>}
    </Stack>
  );
}

Section.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node,
  meta: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
  component: PropTypes.elementType,
  /** The one emphasised sheet of the page: the gradient top edge. */
  emphasis: PropTypes.bool,
  /** The body without the inset: rows that reach the sheet's edges. */
  flush: PropTypes.bool,
  sx: PropTypes.object,
  bodySx: PropTypes.object
};

/**
 * A section of a record: a region sheet with its head and its body. Inside
 * another section it is flat — a head, a hairline, a body — never a box.
 */
export function Section({
  id,
  title,
  meta,
  action,
  children,
  component = 'h2',
  emphasis = false,
  flush = false,
  sx,
  bodySx
}) {
  const theme = useTheme();
  const nested = useContext(SheetContext);
  const headed = Boolean(title || action);
  if (nested) {
    return (
      <Box component="section" id={id} sx={{ minWidth: 0, scrollMarginTop: 112, ...sx }}>
        {headed && (
          <SectionHead title={title} meta={meta} action={action} component={component} />
        )}
        <Box sx={{ pt: headed ? 2 : 0, minWidth: 0, ...bodySx }}>{children}</Box>
      </Box>
    );
  }
  return (
    <SheetContext.Provider value>
      <Box
        component="section"
        id={id}
        sx={{ ...regionSheetSx(theme, { emphasis }), scrollMarginTop: 112, ...sx }}
      >
        {headed && (
          <SectionHead inset title={title} meta={meta} action={action} component={component} />
        )}
        <Box
          sx={{
            minWidth: 0,
            ...(flush ? {} : { px: SHEET_INSET, pt: headed ? 2 : SHEET_INSET, pb: SHEET_INSET }),
            ...bodySx
          }}
        >
          {children}
        </Box>
      </Box>
    </SheetContext.Provider>
  );
}

// ---------------------------------------------------------------------------
/** A sunken region: the economics board, an inset list. No border, no shadow. */
export const wellSx = (theme) => ({
  bgcolor: sunkenOf(theme),
  borderRadius: `${RADIUS.panel}px`,
  px: { xs: 2, sm: 2.5 },
  py: { xs: 2, sm: 2.5 },
  minWidth: 0
});

Well.propTypes = { children: PropTypes.node, sx: PropTypes.object };

export function Well({ children, sx }) {
  const theme = useTheme();
  return <Box sx={{ ...wellSx(theme), ...sx }}>{children}</Box>;
}

/** The one sheet a record may draw where separation has a reason. */
export const sheetSx = (theme) => ({
  bgcolor: 'background.paper',
  border: `1px solid ${hairlineOf(theme)}`,
  borderRadius: `${RADIUS.panel}px`,
  boxShadow: 'none',
  minWidth: 0,
  overflow: 'hidden'
});

// ---------------------------------------------------------------------------

Eyebrow.propTypes = { children: PropTypes.node, sx: PropTypes.object };

/**
 * The group label: 13/600 in the secondary ink, sentence case, naming a
 * group — never a fact. It was the 11px uppercase eyebrow until the owner's
 * verdict on the page (08/10/2026: "I don't like these small descriptions").
 */
export function Eyebrow({ children, sx }) {
  return (
    <Typography
      component="div"
      sx={{
        fontFamily: UNIVERSE_FONT,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: 0,
        color: 'text.secondary',
        ...sx
      }}
    >
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------

Fact.propTypes = {
  label: PropTypes.node,
  children: PropTypes.node,
  href: PropTypes.string,
  empty: PropTypes.string,
  numeric: PropTypes.bool
};

/** One fact: label 116px in the secondary ink, the value right-aligned. */
export function Fact({ label, children, href, empty = 'Not on file', numeric = false }) {
  const theme = useTheme();
  const blank =
    children === null ||
    children === undefined ||
    children === '' ||
    children === '—';
  return (
    <Stack
      data-universe-fact
      direction="row"
      spacing={2}
      alignItems="baseline"
      sx={{
        py: 1,
        borderBottom: `1px solid ${hairlineOf(theme)}`,
        '&:last-of-type': { borderBottom: 0 }
      }}
    >
      <Typography
        component="div"
        sx={{ ...dataSx, width: 116, flex: 'none', color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
        {blank ? (
          <Typography component="div" sx={{ ...dataSx, color: 'text.secondary' }}>
            {empty}
          </Typography>
        ) : href ? (
          <Link
            href={href}
            target={href.startsWith('http') ? '_blank' : undefined}
            rel="noopener"
            underline="hover"
            sx={{
              ...bodyStrongSx,
              color: accentOf(theme),
              wordBreak: 'break-word',
              ...(numeric && tabularSx)
            }}
          >
            {children}
          </Link>
        ) : (
          <Typography
            component="div"
            sx={{ ...bodyStrongSx, wordBreak: 'break-word', ...(numeric && tabularSx) }}
          >
            {children}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

FactGroup.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  sx: PropTypes.object
};

export function FactGroup({ title, children, sx }) {
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      {title && <Eyebrow sx={{ mb: 0.5 }}>{title}</Eyebrow>}
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// HEADS. A SECONDARY surface has a quiet head: label 13/600 in the secondary
// ink on the sunken ground, a hairline beneath. An EMBEDDED surface names
// its columns in meta only where the reader needs them, over a hairline.

export const quietHeadSx = (theme) => ({
  display: 'grid',
  gap: 1.5,
  alignItems: 'center',
  px: 1.5,
  minHeight: 36,
  bgcolor: sunkenOf(theme),
  borderBottom: `1px solid ${hairlineOf(theme)}`
});

export const quietHeadCellSx = () => ({
  fontFamily: UNIVERSE_FONT,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  letterSpacing: 0,
  color: 'text.secondary'
});

export const embeddedHeadSx = (theme) => ({
  display: 'grid',
  gap: 1.5,
  alignItems: 'center',
  px: 1.5,
  pb: 0.75,
  borderBottom: `1px solid ${hairlineOf(theme)}`
});

export const embeddedHeadCellSx = () => metaSx;

/** The rule between the rows of an embedded or secondary surface. */
export const rowRuleOf = (theme) => hairlineOf(theme);

/** A list of rows: hairlines between them, nothing around them. */
export const rowsBoxSx = (theme) => ({
  minWidth: 0,
  borderTop: `1px solid ${hairlineOf(theme)}`
});

/** A row of an embedded surface: the hairline, a whisper of the accent on hover. */
export const rowSx = (theme) => ({
  borderBottom: `1px solid ${hairlineOf(theme)}`,
  '&:last-of-type': { borderBottom: 0 },
  transition: theme.transitions.create('background-color', { duration: MOTION.fast })
});

/** A row in the tone's wash with a 2px edge: the one who took the job. */
export const rowTonedSx = (theme, ink, wash) => ({
  bgcolor: wash,
  boxShadow: `inset 2px 0 0 ${ink}`
});

// A MUI Table drawn as a SECONDARY (quiet head) or EMBEDDED surface, for the
// small tables the page still draws with TableCell: the items of the order,
// the CAT bands, the files of a dialog, the log.
const tableBody = (theme) => ({
  '& .MuiTableCell-body': {
    py: 1.25,
    px: 1.5,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    lineHeight: 1.45,
    color: 'text.primary',
    borderBottom: `1px solid ${hairlineOf(theme)}`,
    verticalAlign: 'top'
  },
  '& .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 0 },
  '& .MuiTableRow-root': {
    transition: theme.transitions.create('background-color', { duration: MOTION.fast })
  },
  '& .MuiTableRow-hover:hover, & .MuiTableRow-root.MuiTableRow-hover:hover': {
    bgcolor: accentTint(theme, 'hover')
  },
  '& .MuiTypography-root': { fontFamily: UNIVERSE_FONT },
  '& .is-numeric': tabularSx
});

export const secondaryTableSx = (theme) => ({
  ...tableBody(theme),
  '& .MuiTableCell-head': {
    py: 0,
    px: 1.5,
    height: 36,
    bgcolor: sunkenOf(theme),
    backgroundImage: 'none',
    color: 'text.secondary',
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    border: 0,
    borderBottom: `1px solid ${hairlineOf(theme)}`,
    boxShadow: 'none !important',
    borderRadius: '0 !important',
    '&:first-of-type': { borderRadius: `${RADIUS.small}px 0 0 0`, boxShadow: 'none' },
    '&:last-of-type': { borderRadius: `0 ${RADIUS.small}px 0 0`, boxShadow: 'none' }
  }
});

/** A picker's table: the quiet head, rows of one line, middles aligned. */
export const pickerTableSx = (theme) => ({
  ...secondaryTableSx(theme),
  '& .MuiTableCell-body': {
    ...secondaryTableSx(theme)['& .MuiTableCell-body'],
    py: 0.75,
    verticalAlign: 'middle'
  },
  // A tick box at MUI's own size is 42px tall and SETS the row's height
  // whatever the cells beside it hold: the one-line rows stayed 61px until
  // its padding came down (09/10/2026).
  '& .MuiCheckbox-root': { p: 0.5 }
});

export const embeddedTableSx = (theme) => ({
  ...tableBody(theme),
  '& .MuiTableCell-head': {
    py: 0.75,
    px: 1.5,
    bgcolor: 'transparent',
    backgroundImage: 'none',
    fontFamily: UNIVERSE_FONT,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: 0,
    textTransform: 'none',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    border: 0,
    borderBottom: `1px solid ${hairlineOf(theme)}`,
    boxShadow: 'none !important',
    borderRadius: '0 !important'
  }
});

// ---------------------------------------------------------------------------
// THE FIELDS OF A FORM (DESIGN.md §16, §18), as one rule spread on the
// dialog or the form that holds them: 36px controls, radius 6, the field
// line, the label above the control instead of floating in it, helper text
// in meta; a multi-line field and a multi-value picker grow as they must.
// The forms keep their fields and their validation; only the geometry is
// the system's.

export const formControlsSx = (theme) => {
  const accent = accentOf(theme);
  const ring = `0 0 0 3px ${alpha(accent, isLight(theme) ? 0.24 : 0.32)}`;
  const secondary = theme.palette.text.secondary;
  return {
    '& .MuiOutlinedInput-root': {
      position: 'relative',
      boxSizing: 'border-box',
      minHeight: 36,
      px: '12px',
      py: 0,
      border: `1px solid ${fieldLineOf(theme)}`,
      borderRadius: `${RADIUS.control}px`,
      bgcolor: 'background.paper',
      color: fieldInkOf(theme),
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.4,
      transition: `border-color ${MOTION.fast}ms, box-shadow ${MOTION.fast}ms`,
      '&:hover': { borderColor: alpha(accent, 0.5) },
      '&.Mui-focused': { borderColor: accent, boxShadow: ring },
      '&.Mui-disabled': { bgcolor: sunkenOf(theme), opacity: 0.8 },
      '&.Mui-error': { borderColor: theme.palette.error.main }
    },
    '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      height: 34,
      p: 0,
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: '34px',
      color: fieldInkOf(theme),
      '&::placeholder': { color: secondary, fontWeight: 400, opacity: 1 }
    },
    '& label[data-shrink=false] + .MuiInputBase-formControl .MuiOutlinedInput-input::placeholder':
      { opacity: '1 !important' },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
      py: 0.75,
      alignItems: 'flex-start'
    },
    // MUI marks a textarea `MuiInputBase-inputMultiline` (the Outlined
    // variant of the name is never set), so this rule used to miss and every
    // multi-line field drew its lines 34px apart.
    '& .MuiOutlinedInput-input.MuiInputBase-inputMultiline': {
      height: 'auto',
      lineHeight: 1.5,
      p: 0
    },
    '& .MuiInputAdornment-positionStart': { ml: 0, mr: 1, color: secondary },
    '& .MuiInputAdornment-positionEnd': { ml: 0.5, mr: -0.5 },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
      p: 0.5,
      color: secondary,
      '&:hover': { color: accent, bgcolor: 'transparent' }
    },
    '& .MuiSelect-select.MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      height: '34px !important',
      minHeight: '0 !important',
      display: 'block',
      lineHeight: '34px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      p: '0 28px 0 0 !important'
    },
    '& .MuiSelect-icon': {
      right: 10,
      top: 'calc(50% - 10px)',
      color: secondary
    },
    // the multi-value pickers (languages, files, termbases) wrap their chips
    '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
      p: '2px 8px 2px 10px',
      minHeight: 36,
      gap: '2px'
    },
    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      height: 30,
      lineHeight: '30px',
      minWidth: 60,
      p: '0 2px'
    },
    '& .MuiAutocomplete-endAdornment': { right: '8px !important' },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      p: 0.5,
      color: secondary,
      '&:hover': { color: accent, bgcolor: 'transparent' }
    },
    '& .MuiAutocomplete-tag, & .MuiAutocomplete-inputRoot .MuiChip-root': {
      height: 24,
      my: '1px',
      borderRadius: `${RADIUS.control}px`,
      fontFamily: UNIVERSE_FONT,
      fontSize: 12,
      fontWeight: 500
    },
    // the label ABOVE the field, in label type; helper and error in meta
    '& .MuiInputLabel-root, & .MuiInputLabel-outlined': {
      position: 'static',
      transform: 'none',
      maxWidth: 'none',
      mb: 0.75,
      pointerEvents: 'auto',
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
      '&.Mui-focused': { color: theme.palette.text.primary },
      '&.Mui-error': { color: theme.palette.text.primary },
      '&.Mui-disabled': { color: theme.palette.text.disabled }
    },
    '& .MuiInputLabel-asterisk': { color: theme.palette.error.main },
    '& .MuiFormHelperText-root': {
      mx: 0,
      mt: 0.5,
      fontFamily: UNIVERSE_FONT,
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.4,
      color: secondary,
      '&.Mui-error': { color: theme.palette.error.main }
    },
    '& .MuiFormLabel-root.MuiFormLabel-root:not(.MuiInputLabel-root)': {
      fontFamily: UNIVERSE_FONT,
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary
    },
    '& .MuiFormControlLabel-label': { fontFamily: UNIVERSE_FONT, fontSize: 13 },
    '& .MuiCheckbox-root': {
      color: theme.palette.text.secondary,
      '&.Mui-checked': { color: accent }
    },
    '& .MuiRadio-root': {
      color: theme.palette.text.secondary,
      '&.Mui-checked': { color: accent }
    },
    // THE RICH TEXT FIELD (§18): Quill inside the field geometry — the
    // field line, radius 6, its toolbar on the sunken ground, everything
    // in the UI face. The editor keeps its own buttons.
    '& .quill': {
      border: `1px solid ${fieldLineOf(theme)}`,
      borderRadius: `${RADIUS.control}px`,
      overflow: 'hidden',
      transition: `border-color ${MOTION.fast}ms`,
      '&:hover': { borderColor: alpha(accent, 0.5) },
      '&:focus-within': { borderColor: accent, boxShadow: ring }
    },
    '& .ql-toolbar.ql-snow': {
      border: 0,
      borderBottom: `1px solid ${fieldLineOf(theme)}`,
      bgcolor: sunkenOf(theme)
    },
    '& .ql-container.ql-snow': { border: 0 },
    '& .ql-editor': {
      fontFamily: UNIVERSE_FONT,
      fontSize: 14,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      minHeight: 160
    },
    '& .ql-editor.ql-blank::before': {
      color: secondary,
      fontStyle: 'normal',
      fontFamily: UNIVERSE_FONT
    },
    '& .MuiRating-iconFilled': { color: accent },
    '& .MuiRating-iconHover': { color: accent }
  };
};

// ---------------------------------------------------------------------------

InlineError.propTypes = {
  message: PropTypes.node,
  onRetry: PropTypes.func,
  sx: PropTypes.object
};

/** A failure is an inline block beside the thing that failed, with Try again. */
export function InlineError({ message, onRetry, sx }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        p: 2,
        borderRadius: `${RADIUS.panel}px`,
        bgcolor: sunkenOf(theme),
        ...sx
      }}
    >
      <Icon
        name="Danger"
        size="md"
        sx={{ color: theme.palette.error.main, flex: 'none', mt: '1px' }}
      />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ ...dataSx, color: 'text.primary' }}>{message}</Typography>
      </Box>
      {onRetry && (
        <Button size="small" sx={ghostButtonSx(theme, 'small')} onClick={onRetry}>
          Try again
        </Button>
      )}
    </Stack>
  );
}

/** A quiet sentence where a list is empty. */
export function EmptyLine({ children, sx }) {
  return (
    <Typography sx={{ ...dataSx, color: 'text.secondary', ...sx }}>{children}</Typography>
  );
}

EmptyLine.propTypes = { children: PropTypes.node, sx: PropTypes.object };

/** The segmented control track (DESIGN.md §18): a sunken track, radius 6. */
export const segmentedTrackSx = (theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  p: '2px',
  borderRadius: `${RADIUS.control}px`,
  bgcolor: sunkenOf(theme),
  maxWidth: '100%',
  flexWrap: 'wrap'
});

/** One segment: paper with the small shadow when chosen. */
export const segmentSx = (theme, active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  height: 28,
  px: 1.25,
  border: 0,
  borderRadius: '4px',
  fontFamily: UNIVERSE_FONT,
  fontSize: 12.5,
  fontWeight: 600,
  lineHeight: 1,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  bgcolor: active ? theme.palette.background.paper : 'transparent',
  boxShadow: active
    ? isLight(theme)
      ? '0 1px 2px rgba(1,38,109,.06), 0 0 0 1px rgba(1,38,109,.04)'
      : '0 1px 2px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.08)'
    : 'none',
  transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
    duration: MOTION.fast
  }),
  '&:hover': { color: theme.palette.text.primary },
  '&:focus-visible': focusRingSx(theme),
  '&:disabled': { cursor: 'default', opacity: 0.45 }
});

/** The HTML of a note, in the body face. */
export const noteHtmlSx = {
  ...dataSx,
  fontSize: 14,
  lineHeight: 1.5,
  wordBreak: 'break-word',
  '& p': { m: 0 },
  '& p + p': { mt: 0.75 },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& a': { color: 'inherit' }
};
